import type { LucideIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  /** Primary message, e.g. "No deals in this view." */
  title: string;
  /** Optional secondary hint below the title. */
  hint?: string;
  /** Optional lucide icon rendered in a muted chip above the title. */
  icon?: LucideIcon;
  /** Optional call-to-action (e.g. a "Register a deal" button) below the hint. */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Standard empty-state panel — dashed hairline, centred icon chip and
 * message. Use wherever a list/table renders "nothing here yet" so empty
 * views read as designed surfaces rather than missing content.
 */
export function EmptyState({ title, hint, icon: Icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-12 text-center",
        className,
      )}
    >
      {Icon ? (
        <span
          aria-hidden
          className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground"
        >
          <Icon className="h-5 w-5" />
        </span>
      ) : null}
      <p className="text-sm font-medium">{title}</p>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}
